/**
 * COSectionViewer - TASK-374: CO Review System
 *
 * Read-only viewer for a single KYC section during compliance review.
 * Renders label-value pairs with optional knowledge graph confidence badges.
 * Supports both flat fields and repeated-entry lists (wealth sources, income, etc.).
 */

import React from 'react';
import {
  FileText,
  ShieldCheck,
  User,
  Briefcase,
  MapPin,
  Building2,
  CreditCard,
  Landmark,
  ScrollText,
  GraduationCap,
  DollarSign,
} from 'lucide-react';
import { ConfidenceBadge } from '../../kyc/ConfidenceBadge';
import { formatValue } from '../../../utils/formatters';
import type { KnowledgeGraphEntry } from '../../../types/complianceWorkflow';

interface FieldDef {
  key: string;
  label: string;
  wide?: boolean;
}

/** Definition for a repeated-entry list within a section */
interface ListDef {
  key: string; // key in kycData that holds the array
  label: string; // heading shown above the list
  titleKey: string; // field within each item used as the card title
  fields: FieldDef[];
}

interface SectionDef {
  label: string;
  icon: React.ElementType;
  fields: FieldDef[];
  list?: ListDef;
}

interface COSectionViewerProps {
  sectionId: string;
  kycData: Record<string, any>;
  knowledgeGraph?: KnowledgeGraphEntry[];
}

const SECTION_CONFIG: Record<string, SectionDef> = {
  personalInfo: {
    label: 'Personal Information',
    icon: User,
    fields: [
      { key: 'first_name', label: 'First Name' },
      { key: 'middle_name', label: 'Middle Name' },
      { key: 'last_name', label: 'Last Name' },
      { key: 'date_of_birth', label: 'Date of Birth' },
      { key: 'place_of_birth', label: 'Place of Birth' },
      { key: 'gender', label: 'Gender' },
      { key: 'marital_status', label: 'Marital Status' },
      { key: 'nationalities', label: 'Nationalities' },
      { key: 'passport_number', label: 'Passport Number' },
      { key: 'national_id', label: 'National ID' },
      { key: 'ssn', label: 'Swiss AHV / SSN' },
    ],
  },
  contactInfo: {
    label: 'Contact Details',
    icon: MapPin,
    fields: [
      { key: 'email_primary', label: 'Email' },
      { key: 'email_secondary', label: 'Secondary Email' },
      { key: 'phone_primary', label: 'Phone' },
      { key: 'phone_secondary', label: 'Secondary Phone' },
      { key: 'residential_address', label: 'Residential Address', wide: true },
      { key: 'mailing_address', label: 'Mailing Address', wide: true },
      { key: 'preferred_contact_method', label: 'Preferred Contact Method' },
      { key: 'relationship_reason', label: 'Relationship Reason', wide: true },
      { key: 'relationship_purpose', label: 'Relationship Purpose', wide: true },
    ],
  },
  employmentInfo: {
    label: 'Employment & Occupation',
    icon: Briefcase,
    fields: [
      { key: 'employment_status', label: 'Employment Status' },
      { key: 'employer_name', label: 'Employer Name' },
      { key: 'occupation', label: 'Occupation' },
      { key: 'employment_industry', label: 'Industry' },
      { key: 'years_employed', label: 'Years Employed' },
      { key: 'annual_income', label: 'Annual Income' },
      { key: 'income_currency', label: 'Income Currency' },
      { key: 'work_address', label: 'Work Address', wide: true },
    ],
  },
  personalBackground: {
    label: 'Personal Background',
    icon: GraduationCap,
    fields: [
      { key: 'education_level', label: 'Education Level' },
      { key: 'institutions_attended', label: 'Institutions Attended', wide: true },
      { key: 'professional_certifications', label: 'Professional Certifications', wide: true },
      { key: 'hobbies_interests', label: 'Hobbies & Interests' },
      { key: 'family_composition', label: 'Family Composition' },
      { key: 'dependents_count', label: 'Number of Dependents' },
      { key: 'spouse_employment_status', label: 'Spouse Employment Status' },
    ],
  },
  taxInfo: {
    label: 'Tax Information',
    icon: Landmark,
    fields: [
      { key: 'tax_residence', label: 'Tax Residence' },
      { key: 'tax_id', label: 'Tax ID (TIN)' },
      { key: 'us_person', label: 'US Person (FATCA)' },
      { key: 'fatca_status', label: 'FATCA Status' },
      { key: 'us_ssn', label: 'US SSN' },
      { key: 'tax_exempt', label: 'Tax Exempt' },
      { key: 'tax_exempt_reason', label: 'Tax Exempt Reason', wide: true },
      { key: 'additional_tax_residencies', label: 'Additional Tax Residencies', wide: true },
    ],
  },
  adverseMedia: {
    label: 'Adverse Media & PEP',
    icon: ShieldCheck,
    fields: [
      { key: 'is_pep', label: 'Politically Exposed Person' },
      { key: 'pep_type', label: 'PEP Type' },
      { key: 'pep_position', label: 'PEP Position' },
      { key: 'pep_country', label: 'PEP Country' },
      { key: 'pep_start_date', label: 'PEP Start Date' },
      { key: 'pep_end_date', label: 'PEP End Date' },
      { key: 'is_rca', label: 'Relative / Close Associate (RCA)' },
      { key: 'rca_relationship', label: 'RCA Relationship' },
      { key: 'rca_pep_name', label: 'RCA PEP Name' },
      { key: 'has_adverse_media', label: 'Adverse Media Found' },
      { key: 'adverse_media_type', label: 'Adverse Media Type' },
      { key: 'adverse_media_details', label: 'Adverse Media Details', wide: true },
      { key: 'sanctions_screened', label: 'Sanctions Screened' },
      { key: 'sanctions_hit', label: 'Sanctions Hit' },
      { key: 'sanctions_details', label: 'Sanctions Details', wide: true },
      { key: 'reputational_risk', label: 'Reputational Risk' },
      { key: 'insider_flag', label: 'Insider Flag' },
    ],
    list: {
      key: 'board_positions_list',
      label: 'Board Positions',
      titleKey: 'company',
      fields: [
        { key: 'company', label: 'Company' },
        { key: 'position', label: 'Position' },
        { key: 'country', label: 'Country' },
        { key: 'current', label: 'Current' },
        { key: 'is_publicly_traded', label: 'Publicly Traded' },
        { key: 'isin', label: 'ISIN' },
      ],
    },
  },
  businessActivities: {
    label: 'Business Activities',
    icon: Building2,
    fields: [],
    list: {
      key: 'businesses_list',
      label: 'Businesses',
      titleKey: 'business_name',
      fields: [
        { key: 'business_name', label: 'Business Name' },
        { key: 'business_type', label: 'Business Type' },
        { key: 'registration_number', label: 'Registration Number' },
        { key: 'registration_country', label: 'Registration Country' },
        { key: 'industry', label: 'Industry' },
        { key: 'industry_risk_level', label: 'Industry Risk Level' },
        { key: 'ownership_percentage', label: 'Ownership (%)' },
        { key: 'high_risk_countries', label: 'High-Risk Countries' },
        { key: 'years_in_business', label: 'Years in Business' },
        { key: 'number_of_employees', label: 'Employees' },
        { key: 'annual_revenue', label: 'Annual Revenue' },
        { key: 'revenue_currency', label: 'Revenue Currency' },
        { key: 'cash_intensive', label: 'Cash Intensive' },
        { key: 'cross_border_transactions', label: 'Cross-Border Transactions' },
      ],
    },
  },
  wealthSources: {
    label: 'Source of Wealth',
    icon: CreditCard,
    fields: [
      { key: 'primary_wealth_source', label: 'Primary Source of Wealth' },
      { key: 'wealth_narrative', label: 'Wealth Narrative', wide: true },
      { key: 'total_net_worth', label: 'Total Net Worth' },
      { key: 'liquid_net_worth', label: 'Liquid Net Worth' },
      { key: 'net_worth_currency', label: 'Currency' },
      { key: 'asset_breakdown_cash', label: 'Cash & Equivalents (%)' },
      { key: 'asset_breakdown_securities', label: 'Securities (%)' },
      { key: 'asset_breakdown_real_estate', label: 'Real Estate (%)' },
      { key: 'asset_breakdown_business_equity', label: 'Business Equity (%)' },
      { key: 'asset_breakdown_commodities', label: 'Commodities (%)' },
      { key: 'asset_breakdown_crypto', label: 'Cryptocurrency (%)' },
      { key: 'asset_breakdown_other', label: 'Other Assets (%)' },
      { key: 'inheritance_expected', label: 'Inheritance Expected' },
      { key: 'inheritance_details', label: 'Inheritance Details', wide: true },
      { key: 'unusual_transactions', label: 'Unusual Transactions' },
      { key: 'unusual_transaction_details', label: 'Details', wide: true },
      { key: 'source_documentation', label: 'Source Documentation', wide: true },
    ],
    list: {
      key: 'wealth_sources_list',
      label: 'Detailed Source Breakdown',
      titleKey: 'source_type',
      fields: [
        { key: 'source_type', label: 'Source Type' },
        { key: 'description', label: 'Description', wide: true },
        { key: 'percentage', label: 'Percentage (%)' },
        { key: 'amount', label: 'Amount' },
        { key: 'asset_type', label: 'Asset Type' },
        { key: 'asset_location', label: 'Asset Location' },
        { key: 'acquisition_start_date', label: 'Start Date' },
        { key: 'acquisition_end_date', label: 'End Date' },
        { key: 'is_ongoing', label: 'Ongoing' },
        { key: 'liquidity_level', label: 'Liquidity Level' },
        { key: 'documented', label: 'Documented' },
        { key: 'verification_notes', label: 'Verification Notes', wide: true },
        { key: 'supporting_document_types', label: 'Supporting Document Types' },
        { key: 'uploaded_document_ids', label: 'Uploaded Document IDs' },
      ],
    },
  },
  incomeSources: {
    label: 'Income & Assets',
    icon: DollarSign,
    fields: [
      { key: 'total_annual_income', label: 'Total Annual Income' },
      { key: 'income_currency', label: 'Income Currency' },
      { key: 'primary_income_type', label: 'Primary Income Type' },
      { key: 'expected_income_change', label: 'Expected Income Change' },
      { key: 'expected_change_details', label: 'Change Details', wide: true },
      { key: 'retirement_income', label: 'Retirement Income' },
      { key: 'retirement_income_details', label: 'Retirement Details', wide: true },
      { key: 'other_financial_support', label: 'Other Financial Support' },
      { key: 'other_support_details', label: 'Support Details', wide: true },
    ],
    list: {
      key: 'income_sources_list',
      label: 'Income Sources',
      titleKey: 'income_type',
      fields: [
        { key: 'income_type', label: 'Income Type' },
        { key: 'income_description', label: 'Description', wide: true },
        { key: 'annual_amount', label: 'Annual Amount' },
        { key: 'currency', label: 'Currency' },
        { key: 'frequency', label: 'Frequency' },
        { key: 'source_name', label: 'Source Name' },
        { key: 'source_country', label: 'Source Country' },
        { key: 'start_date', label: 'Start Date' },
        { key: 'is_ongoing', label: 'Ongoing' },
        { key: 'verified', label: 'Verified' },
        { key: 'supporting_documents', label: 'Supporting Documents' },
        { key: 'stability_rating', label: 'Stability' },
        { key: 'expected_continuity', label: 'Expected Continuity' },
      ],
    },
  },
  reviewSubmit: {
    label: 'Review & Submit',
    icon: ScrollText,
    fields: [
      { key: 'status', label: 'Profile Status' },
      { key: 'risk_level', label: 'Risk Level' },
      { key: 'risk_score', label: 'Risk Score' },
      { key: 'data_source', label: 'Data Source' },
      { key: 'verification_level', label: 'Verification Level' },
      { key: 'kyc_completed_date', label: 'KYC Completed Date' },
      { key: 'advisor_notes', label: 'Advisor Notes', wide: true },
    ],
  },
};

/** Renders a single field card */
const FieldCard: React.FC<{
  field: FieldDef;
  value: unknown;
  kgEntry?: KnowledgeGraphEntry;
}> = ({ field, value, kgEntry }) => {
  const displayValue = formatValue(value);
  const hasValue = displayValue !== '--';

  return (
    <div
      className={`p-3 rounded-lg border ${field.wide ? 'md:col-span-2' : ''} ${
        hasValue ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
            {field.label}
          </p>
          <p
            className={`text-sm whitespace-pre-wrap ${
              hasValue ? 'text-gray-900 font-medium' : 'text-gray-400 italic'
            }`}
          >
            {displayValue}
          </p>
        </div>

        {kgEntry && (
          <div className="flex flex-col items-end gap-1 shrink-0">
            <ConfidenceBadge
              confidence={kgEntry.confidence}
              fieldName={field.label}
              size="sm"
            />
            {kgEntry.extraction_method && (
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600"
                title={`Source: ${kgEntry.extraction_method}`}
              >
                {kgEntry.extraction_method}
              </span>
            )}
            {kgEntry.source_document_name && (
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-50 text-blue-600 max-w-[120px] truncate"
                title={kgEntry.source_document_name}
              >
                {kgEntry.source_document_name}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const COSectionViewer: React.FC<COSectionViewerProps> = ({
  sectionId,
  kycData,
  knowledgeGraph,
}) => {
  const config = SECTION_CONFIG[sectionId];

  if (!config) {
    return (
      <div className="p-6 text-center text-gray-500">
        <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
        <p className="text-sm">Unknown section: {sectionId}</p>
      </div>
    );
  }

  const SectionIcon = config.icon;

  const getKGEntry = (fieldKey: string): KnowledgeGraphEntry | undefined => {
    if (!knowledgeGraph) return undefined;
    return knowledgeGraph.find(
      (entry) => entry.field_path === fieldKey || entry.field_path.endsWith(`.${fieldKey}`)
    );
  };

  const listItems: Record<string, any>[] = config.list
    ? (kycData[config.list.key] || [])
    : [];

  const hasAnyFlatField = config.fields.some((f) => formatValue(kycData[f.key]) !== '--');
  const hasListItems = listItems.length > 0;

  return (
    <div className="space-y-4">
      {/* Section header */}
      <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
        <div className="p-2 bg-blue-50 rounded-lg">
          <SectionIcon className="w-5 h-5 text-blue-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">{config.label}</h3>
      </div>

      {/* Flat fields grid */}
      {config.fields.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {config.fields.map((field) => (
            <FieldCard
              key={field.key}
              field={field}
              value={kycData[field.key]}
              kgEntry={getKGEntry(field.key)}
            />
          ))}
        </div>
      )}

      {/* Repeated-entry list */}
      {config.list && (
        <div className="space-y-3 mt-4">
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
            {config.list.label}
            {hasListItems && (
              <span className="ml-2 text-xs font-normal text-gray-500">
                ({listItems.length} {listItems.length === 1 ? 'entry' : 'entries'})
              </span>
            )}
          </h4>

          {!hasListItems && (
            <p className="text-sm text-gray-400 italic py-2">No entries</p>
          )}

          {listItems.map((item, idx) => {
            const title = item[config.list!.titleKey] || `Entry ${idx + 1}`;
            return (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                {/* Entry header */}
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                  <span className="text-sm font-semibold text-gray-800">
                    {formatValue(title)}
                  </span>
                </div>
                {/* Entry fields */}
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {config.list!.fields.map((field) => {
                    const val = item[field.key];
                    const display = formatValue(val);
                    const hasVal = display !== '--';

                    return (
                      <div
                        key={field.key}
                        className={field.wide ? 'md:col-span-2' : ''}
                      >
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-0.5">
                          {field.label}
                        </p>
                        <p
                          className={`text-sm ${
                            hasVal ? 'text-gray-900' : 'text-gray-400 italic'
                          }`}
                        >
                          {display}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty section notice */}
      {!hasAnyFlatField && !hasListItems && (
        <div className="text-center py-6 text-gray-400">
          <p className="text-sm">No data available for this section</p>
        </div>
      )}
    </div>
  );
};

export default COSectionViewer;
